import { Component, OnInit } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from 'src/environments/environment';

interface AppInfo {
  code: string;
  name: string;
  desc: string;
  icon: string;
}

@Component({
  selector: 'app-dashboard',
  templateUrl: './dashboard.component.html',
  styleUrls: ['./dashboard.component.css']
})
export class DashboardComponent implements OnInit {
  // Available Rating Systems
  apps: AppInfo[] = [
    { code: 'ALL', name: 'ALL FRAMEWORKS', desc: 'Unified metrics and database explorer across all GRIHA rating frameworks', icon: '📊' },
    { code: 'G001V001', name: 'SVAGRIHA', desc: 'Simple Versatile Affordable GRIHA for small developments', icon: '🏡' },
    { code: 'G002V001', name: 'SCHOOL GRIHA', desc: 'Green rating for school buildings', icon: '🏫' },
    { code: 'G003V001', name: 'JAN GRIHA', desc: 'Green rating for mass housing projects', icon: '🏢' },
    { code: 'G004V001', name: 'WATER GRIHA', desc: 'Water efficiency and management rating', icon: '💧' },
    { code: 'G007V001', name: 'WASTE GRIHA', desc: 'Solid waste management rating system', icon: '♻️' }
  ];

  // Selected rating system
  activeAppCode = 'ALL';

  // Sidebar collapse state
  sidebarCollapsed = false;

  // Backend state
  stats: any = null;
  projects: any[] = [];
  loading = false;
  errorMsg = '';

  // Selected project for details drawer
  selectedProject: any = null;

  // Filters (Computed on frontend for instant feedback)
  searchQuery = '';
  statusFilter = '';
  stateFilter = '';
  appTypeFilter = '';
  projectModeFilter = '';

  // Pagination
  currentPage = 1;
  pageSize = 10;

  private readonly dashboardApiBase = environment.dashboardApiBaseUrl.replace(/\/$/, '');

  constructor(private http: HttpClient) {}

  ngOnInit(): void {
    this.loadDashboardData(this.activeAppCode);
  }

  // Getters replacing computed properties
  get availableStates(): string[] {
    const list = this.projects.map(p => p.state).filter(Boolean);
    return Array.from(new Set(list)).sort();
  }

  get availableApps(): AppInfo[] {
    return this.apps.filter(a => a.code !== 'ALL');
  }

  get availableStatuses(): string[] {
    const list = this.projects.map(p => p.status).filter(Boolean);
    return Array.from(new Set(list)).sort();
  }

  get filteredProjects(): any[] {
    let list = this.projects;
    const query = this.searchQuery.trim().toLowerCase();
    const status = this.statusFilter;
    const state = this.stateFilter;
    const appType = this.appTypeFilter;

    if (query) {
      list = list.filter(p => 
        (p.name && p.name.toLowerCase().includes(query)) ||
        (p.project_code && p.project_code.toLowerCase().includes(query)) ||
        (p.city && p.city.toLowerCase().includes(query)) ||
        (p.organization && p.organization.toLowerCase().includes(query))
      );
    }

    if (status) {
      list = list.filter(p => p.status === status);
    }

    if (state) {
      list = list.filter(p => p.state === state);
    }

    if (appType) {
      list = list.filter(p => p.app?.application_code === appType);
    }

    const mode = this.projectModeFilter;
    if (mode === 'live') {
      list = list.filter(p => !this.isTestProject(p));
    } else if (mode === 'demo') {
      list = list.filter(p => this.isTestProject(p));
    }

    return list;
  }

  get paginatedProjects(): any[] {
    const list = this.filteredProjects;
    const start = (this.currentPage - 1) * this.pageSize;
    return list.slice(start, start + this.pageSize);
  }

  get totalPages(): number {
    return Math.ceil(this.filteredProjects.length / this.pageSize) || 1;
  }

  get getTestVsRealDonutData(): any[] {
    const allProjects = this.projects;
    if (allProjects.length === 0) return [];

    const circumference = 2 * Math.PI * 100;
    const testCount = allProjects.filter(p => this.isTestProject(p)).length;
    const realCount = allProjects.length - testCount;
    const total = allProjects.length;

    const realLen = (realCount / total) * circumference;
    const testLen = (testCount / total) * circumference;

    return [
      { label: 'Live', count: realCount,
        percentage: Math.round((realCount / total) * 100),
        color: '#0ea5e9',
        dashArray: `${realLen.toFixed(2)} ${circumference.toFixed(2)}`,
        dashOffset: '0' },
      { label: 'Demo', count: testCount,
        percentage: Math.round((testCount / total) * 100),
        color: '#f97316',
        dashArray: `${testLen.toFixed(2)} ${circumference.toFixed(2)}`,
        dashOffset: `${(-realLen).toFixed(2)}` },
    ].filter(s => s.count > 0);
  }

  get getFrameworkTypeData(): any[] {
    const statsData = this.stats;
    if (!statsData || !statsData.testVsRealByApp) return [];

    const circumference = 2 * Math.PI * 45;

    const frameworks = [
      { code: 'G001V001', name: 'SVAGRIHA',      icon: '🏡' },
      { code: 'G002V001', name: 'SCHOOL GRIHA',  icon: '🏫' },
      { code: 'G003V001', name: 'JAN GRIHA',     icon: '🏢' },
      { code: 'G004V001', name: 'WATER GRIHA',   icon: '💧' },
      { code: 'G007V001', name: 'WASTE GRIHA',   icon: '♻️' },
    ];

    return frameworks.map(fw => {
      const counts = statsData.testVsRealByApp[fw.code] || { test: 0, real: 0 };
      const total = counts.test + counts.real;
      if (total === 0) return { ...fw, total: 0, test: 0, real: 0, slices: [] as any[] };

      const testCount = counts.test;
      const realCount = counts.real;

      const realLen  = (realCount / total) * circumference;
      const testLen  = (testCount / total) * circumference;

      return {
        ...fw,
        total,
        test: testCount,
        real: realCount,
        slices: [
          { label: 'Live', count: realCount, color: '#0ea5e9',
            dashArray: `${realLen.toFixed(2)} ${circumference.toFixed(2)}`,
            dashOffset: '0' },
          { label: 'Demo', count: testCount, color: '#f97316',
            dashArray: `${testLen.toFixed(2)} ${circumference.toFixed(2)}`,
            dashOffset: `${(-realLen).toFixed(2)}` },
        ].filter(s => s.count > 0)
      };
    });
  }

  get getAppChartTicks(): any[] {
    const raw = this.stats?.byApp || {};
    const maxVal = Math.max(...Object.values(raw) as number[], 1);
    return [
      { y: 50, label: maxVal },
      { y: 100, label: Math.round(maxVal * 0.75) },
      { y: 150, label: Math.round(maxVal * 0.5) },
      { y: 200, label: Math.round(maxVal * 0.25) },
      { y: 250, label: 0 }
    ];
  }

  getShortName(name: string): string {
    if (!name) return 'N/A';
    const u = name.toUpperCase();
    if (u.includes('INTERIOR-DETAILED')) return 'INT-DET';
    if (u.includes('INTERIOR-LITE')) return 'INT-LITE';
    if (u.includes('SCHOOLGRIHA')) return 'SCHOOL';
    if (u.includes('WASTEGRIHA')) return 'WASTE';
    if (u.includes('WATERGRIHA')) return 'WATER';
    return u;
  }

  get getAppChartData(): any[] {
    const raw = this.stats?.byApp || {};
    const keys = Object.keys(raw).sort();
    if (keys.length === 0) return [];
    const maxVal = Math.max(...Object.values(raw) as number[], 1);
    const n = keys.length;
    const plotWidth = 480;
    const step = plotWidth / n;
    
    const appColors: Record<string, string> = {
      'INTERIOR-DETAILED': '#3d5a45',
      'INTERIOR-LITE': '#5f8a69',
      'JANGRIHA': '#2d5f75',
      'SCHOOLGRIHA': '#78866b',
      'SVAGRIHA': '#a87858',
      'UNKNOWN': '#6e7a8a',
      'WASTEGRIHA': '#d4af37',
      'WATERGRIHA': '#287a6c'
    };

    return keys.map((key, idx) => {
      const val = raw[key] || 0;
      const height = (val / maxVal) * 200;
      const x = 55 + idx * step;
      const barWidth = Math.min(step * 0.5, 36);
      return {
        key,
        value: val,
        height,
        x: x + (step - barWidth) / 2,
        textX: x + step / 2,
        y: 250 - height,
        percentage: Math.round((val / (this.stats?.totalProjects || 1)) * 100),
        color: appColors[key.toUpperCase()] || '#10b981'
      };
    });
  }

  get getStatusPieChartData(): any[] {
    const raw = this.stats?.byStatus || {};
    const total = this.stats?.totalProjects || 0;
    if (total === 0) return [];

    const groups = [
      {
        key: 'Certified / Closed',
        statuses: ['CLOSED', 'CERTIFIED', 'COMPLETED'],
        color: '#10b981',
        count: 0
      },
      {
        key: 'Initiated',
        statuses: ['INITIATED'],
        color: '#3b82f6',
        count: 0
      },
      {
        key: 'In Progress',
        statuses: ['INPROGRESS', 'REGISTERED', 'PENDING_APPROVAL', 'REAPPEAL_APPROVED', 'RENEWAL-PENDING', 'RENEWAL APPLIED', 'RENEWAL-APPLIED'],
        color: '#f59e0b',
        count: 0
      },
      {
        key: 'Submitted / Pending',
        statuses: ['SUBMITTED', 'PENDING', 'PENDING_SUBMISSION'],
        color: '#8b5cf6',
        count: 0
      },
      {
        key: 'Others',
        statuses: ['DELETED', 'REJECTED', 'UNKNOWN'],
        color: '#64748b',
        count: 0
      }
    ];

    Object.entries(raw).forEach(([status, val]) => {
      const upperStatus = status.toUpperCase().replace(/\s+/g, '-');
      const count = val as number;
      
      let matched = false;
      for (const group of groups) {
        if (group.statuses.includes(upperStatus) || group.statuses.includes(status.toUpperCase())) {
          group.count += count;
          matched = true;
          break;
        }
      }
      if (!matched) {
        groups[groups.length - 1].count += count;
      }
    });

    const activeGroups = groups.filter(g => g.count > 0);
    const circumference = 2 * Math.PI * 100;
    let accumulatedOffset = 0;

    return activeGroups.map(group => {
      const percentage = (group.count / total) * 100;
      const strokeLength = (group.count / total) * circumference;
      const strokeOffset = accumulatedOffset;
      accumulatedOffset += strokeLength;

      return {
        key: group.key,
        count: group.count,
        percentage: Math.round(percentage),
        color: group.color,
        dashArray: `${strokeLength.toFixed(2)} ${circumference.toFixed(2)}`,
        dashOffset: `${(-strokeOffset).toFixed(2)}`
      };
    });
  }

  getActiveApp(): AppInfo {
    return this.apps.find(a => a.code === this.activeAppCode) || this.apps[0];
  }

  selectApp(code: string): void {
    this.activeAppCode = code;
    this.currentPage = 1;
    this.searchQuery = '';
    this.statusFilter = '';
    this.stateFilter = '';
    this.projectModeFilter = '';
    this.appTypeFilter = '';
    this.loadDashboardData(code);
  }

  onSearchInput(event: Event): void {
    this.searchQuery = (event.target as HTMLInputElement).value;
    this.currentPage = 1;
  }

  onStatusChange(event: Event): void {
    this.statusFilter = (event.target as HTMLSelectElement).value;
    this.currentPage = 1;
  }

  onStateChange(event: Event): void {
    this.stateFilter = (event.target as HTMLSelectElement).value;
    this.currentPage = 1;
  }

  onProjectModeChange(event: Event): void {
    this.projectModeFilter = (event.target as HTMLSelectElement).value;
    this.currentPage = 1;
  }

  onAppTypeChange(event: Event): void {
    this.appTypeFilter = (event.target as HTMLSelectElement).value;
    this.currentPage = 1;
  }

  onPageSizeChange(event: Event): void {
    this.pageSize = Number((event.target as HTMLSelectElement).value);
    this.currentPage = 1;
  }

  prevPage(): void {
    if (this.currentPage > 1) {
      this.currentPage--;
    }
  }

  nextPage(): void {
    if (this.currentPage < this.totalPages) {
      this.currentPage++;
    }
  }

  viewProjectDetails(project: any): void {
    this.selectedProject = project;
  }

  closeDrawer(): void {
    this.selectedProject = null;
  }

  exportToCSV(): void {
    const list = this.filteredProjects;
    if (list.length === 0) return;

    const headers = ['Project Code', 'Project Name', 'Building Type', 'City', 'State', 'Status', 'Project Mode', 'Organization', 'Registered Date'];
    const rows = list.map(p => [
      p.project_code || 'N/A',
      `"${p.name?.replace(/"/g, '""') || ''}"`,
      p.type || 'N/A',
      p.city || 'N/A',
      p.state || 'N/A',
      p.status || 'N/A',
      this.isTestProject(p) ? 'DEMO' : 'LIVE',
      `"${p.organization?.replace(/"/g, '""') || ''}"`,
      p.registered_date ? new Date(p.registered_date).toLocaleDateString('en-IN') : 'N/A'
    ]);

    const csvContent = [headers.join(','), ...rows.map(e => e.join(','))].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `${this.getActiveApp().name}_Projects_Report.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  private async loadDashboardData(appCode: string): Promise<void> {
    this.loading = true;
    this.errorMsg = '';
    
    try {
      const statsJson = await this.http
        .get<any>(`${this.dashboardApiBase}/projects/stats`, {
          params: { applicationCode: appCode },
        })
        .toPromise();

      if (statsJson.success) {
        this.stats = statsJson.data;
      } else {
        throw new Error(statsJson.message || 'Unknown stats retrieval error');
      }

      const listJson = await this.http
        .get<any>(`${this.dashboardApiBase}/projects`, {
          params: { applicationCode: appCode, list_size: '2500' },
        })
        .toPromise();

      if (listJson.success) {
        this.projects = listJson.data;
      } else {
        throw new Error(listJson.message || 'Unknown projects list error');
      }

    } catch (err: any) {
      console.error(err);
      this.errorMsg = err.error?.message || err.message || 'Failed to load dashboard data. Please try again later.';
    } finally {
      this.loading = false;
    }
  }

  formatDate(dateStr: string): string {
    if (!dateStr) return 'N/A';
    try {
      return new Date(dateStr).toLocaleDateString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      });
    } catch {
      return dateStr;
    }
  }

  getMaxChartValue(): number {
    if (!this.stats || !this.stats.byApp) return 1;
    const values = Object.values(this.stats.byApp) as number[];
    return Math.max(...values, 1);
  }

  isTestProject(proj: any): boolean {
    if (proj.is_test !== undefined && proj.is_test !== null) {
      return proj.is_test;
    }
    return /^(test|demo)/i.test(proj.name || '');
  }

  async toggleProjectType(proj: any): Promise<void> {
    const newIsTest = !this.isTestProject(proj);
    try {
      await this.http
        .patch(`${this.dashboardApiBase}/projects/${proj._id}/type`, {
          is_test: newIsTest,
        })
        .toPromise();

      // Update local projects list
      this.projects = this.projects.map(p => {
        if (p._id === proj._id) {
          return { ...p, is_test: newIsTest };
        }
        return p;
      });

      // Reload stats to update the donut charts dynamically
      await this.loadDashboardData(this.activeAppCode);
    } catch (err) {
      console.error(err);
      alert('Error updating project mode. Please try again later.');
    }
  }
}
